import { useDistanceToTopOfPageMotionValue } from '@/hooks/framer/useDistanceToTopOfPageMotionValue';
import { useMobileTabBarHeightMotionValue } from '@/hooks/framer/useMobileTabBarHeightMotionValue';
import { useScrollYMotionValue } from '@/hooks/framer/useScrollYMotionValue';
import { useViewportHeightMotionValue } from '@/hooks/framer/useViewportHeightMotionValue';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import { useSpring, useTransform } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
export const useIsNavigatingWithKeyboard = () => {
    const [isKeyboardUser, setIsKeyboardUser] = useState(false);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Tab' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                setIsKeyboardUser(true);
                window.removeEventListener('keydown', handleKeyDown);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    return isKeyboardUser;
};
/**
 * Hook to detect iOS devices using multiple reliable checks
 * @returns boolean indicating if the current device is running iOS
 */
export const useIsIOS = () => {
    const [isIOS, setIsIOS] = useState(false);
    useEffect(() => {
        // Using multiple detection methods for better reliability
        const checkIsIOS = () => {
            // Check 1: User Agent (most reliable)
            const ua = navigator.userAgent.toLowerCase();
            // MSStream helps exclude IE11 which can have iOS in UA
            const isIOSByUA = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
            // Check 2: CSS-based detection
            const isIOSByCSS = CSS.supports('-webkit-touch-callout', 'none') && !CSS.supports('(-moz-appearance: none)');
            return isIOSByUA || isIOSByCSS;
        };
        setIsIOS(checkIsIOS());
    }, []);
    return isIOS;
};
/**
 * Hook to detect Android devices using User Agent
 * @returns boolean indicating if the current device is running Android
 */
export const useIsAndroid = () => {
    const [isAndroid, setIsAndroid] = useState(false);
    useEffect(() => {
        const checkIsAndroid = () => {
            const ua = navigator.userAgent.toLowerCase();
            return /android/.test(ua);
        };
        setIsAndroid(checkIsAndroid());
    }, []);
    return isAndroid;
};
export const useShouldAnimate = () => {
    /*
     * If the user is navigating with the keyboard we also want to disable the animation
     * because the dummy cards will be read by the screen reader.
     */
    const isNavigatingWithKeyboard = useIsNavigatingWithKeyboard();
    /*
     * On smaller screens there isn't enough space to do the animation
     */
    const viewport = useVisualViewport();
    const enoughSpaceToAnimate = viewport ? viewport.width * viewport.height >= 250000 : true;
    /*
     * On mobiles we don't want to run the animation because
     * extending the cards out horizontally is barely visible due to screen width
     */
    const breakpoint = useBreakpoint();
    const isMobile = breakpoint === 'base' || breakpoint === 'xs';
    return enoughSpaceToAnimate && !isNavigatingWithKeyboard && !isMobile;
};
const useStiffSpring = (value) => useSpring(value, { stiffness: 100, damping: 25, restDelta: 0.001 });
export const useContainerMinHeight = () => {
    const containerRef = useRef(null);
    const scrollY = useScrollYMotionValue();
    const mobileTabBarHeight = useMobileTabBarHeightMotionValue();
    const viewportHeight = useStiffSpring(useViewportHeightMotionValue());
    const containerDistanceToTopOfPage = useDistanceToTopOfPageMotionValue(containerRef);
    return {
        ref: containerRef,
        initial: 'min-h-[100vh]',
        animations: {
            minHeight: useTransform(() => `${viewportHeight.get() -
                mobileTabBarHeight.get() -
                Math.min(200, Math.max(0, containerDistanceToTopOfPage.get() - scrollY.get()))}px`),
        },
    };
};
